import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSend, FiUser, FiClock } from 'react-icons/fi';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';

interface TicketMessage {
  message_id: string;
  ticket_id: string;
  user_id: string;
  message: string;
  is_staff_reply: boolean;
  created_at: string;
  first_name: string;
  last_name: string;
  user_type: string;
}

interface TicketDetails {
  ticket_id: string;
  ticket_number: string;
  user_id: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_to_lastname: string | null;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  messages: TicketMessage[];
}

const SupportTicketDetails: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [staffList, setStaffList] = useState<any[]>([]);

  useEffect(() => {
    fetchTicketDetails();
    fetchStaffList();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/support/tickets/${ticketId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setTicket(result.data);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/staff`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (result.success) {
        setStaffList(result.data);
      }
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/support/tickets/${ticketId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: replyMessage }),
        }
      );

      const result = await response.json();
      if (result.success) {
        setReplyMessage('');
        fetchTicketDetails(); // Refresh to show new message
      } else {
        alert(result.message || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('An error occurred while sending the reply');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateTicket = async (updates: any) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/support/tickets/${ticketId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updates),
        }
      );

      const result = await response.json();
      if (result.success) {
        fetchTicketDetails(); // Refresh ticket details
      } else {
        alert(result.message || 'Failed to update ticket');
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('An error occurred while updating the ticket');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'normal':
        return 'bg-blue-100 text-blue-800';
      case 'low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-600">Ticket not found</p>
          <button
            onClick={() => navigate('/admin/support')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Back to Support Tickets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          onClick={() => navigate('/admin/support')}
          variant="ghost"
          icon={<FiArrowLeft />}
          className="mb-4"
        >
          Back to Tickets
        </Button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">{ticket.subject}</h1>
            <p className="text-gray-600">Ticket #{ticket.ticket_number}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Messages */}
        <div className="lg:col-span-2">
          {/* Messages */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Conversation</h2>
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {ticket.messages.map((message) => (
                <div
                  key={message.message_id}
                  className={`flex gap-3 ${
                    message.is_staff_reply ? 'flex-row-reverse' : 'flex-row'
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      message.is_staff_reply
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <FiUser />
                  </div>
                  <div
                    className={`flex-1 max-w-2xl ${
                      message.is_staff_reply ? 'text-right' : 'text-left'
                    }`}
                  >
                    <div
                      className={`inline-block p-4 rounded-lg ${
                        message.is_staff_reply
                          ? 'bg-blue-50 text-left'
                          : 'bg-gray-50 text-left'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">
                          {message.first_name} {message.last_name}
                        </span>
                        {message.is_staff_reply && (
                          <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">
                            Staff
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                      <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                        <FiClock size={12} />
                        {new Date(message.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reply Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Reply</h3>
            <form onSubmit={handleSendReply}>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg resize-none"
                rows={4}
                placeholder="Type your reply..."
                disabled={ticket.status === 'closed'}
              />
              <div className="flex justify-end mt-4">
                <Button
                  type="submit"
                  disabled={sending || !replyMessage.trim() || ticket.status === 'closed'}
                  variant="primary"
                  loading={sending}
                  icon={<FiSend />}
                >
                  Send Reply
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar - Ticket Info */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Customer</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">
                  {ticket.customer_first_name} {ticket.customer_last_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{ticket.customer_email}</p>
              </div>
            </div>
          </div>

          {/* Ticket Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Ticket Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Status</label>
                <select
                  value={ticket.status}
                  onChange={(e) => handleUpdateTicket({ status: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Priority</label>
                <select
                  value={ticket.priority}
                  onChange={(e) => handleUpdateTicket({ priority: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Category</label>
                <select
                  value={ticket.category}
                  onChange={(e) => handleUpdateTicket({ category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="Getting Started">Getting Started</option>
                  <option value="Loan Process">Loan Process</option>
                  <option value="Repayment">Repayment</option>
                  <option value="Account">Account</option>
                  <option value="Technical">Technical</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Assign To</label>
                <select
                  value={ticket.assigned_to || ''}
                  onChange={(e) => handleUpdateTicket({ assigned_to: e.target.value || null })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Unassigned</option>
                  {staffList.map((staff) => (
                    <option key={staff.user_id} value={staff.user_id}>
                      {staff.first_name} {staff.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-4 border-t">
                <div className="text-sm text-gray-600 mb-1">Created</div>
                <div className="font-medium">
                  {new Date(ticket.created_at).toLocaleString()}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-600 mb-1">Last Updated</div>
                <div className="font-medium">
                  {new Date(ticket.updated_at).toLocaleString()}
                </div>
              </div>

              {ticket.closed_at && (
                <div>
                  <div className="text-sm text-gray-600 mb-1">Closed</div>
                  <div className="font-medium">
                    {new Date(ticket.closed_at).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetails;
